// ── Core Types ──

export interface ChildProfile {
  id: string;
  name: string;
  avatar: string; // emoji
  ageGroup: "junior" | "pro";
  isPaused: boolean;
}

export interface AppItem {
  id: string;
  name: string;
  icon: string; // emoji
  category: "earn" | "play";
}

export interface RatioConfig {
  earnMinutes: number;
  playMinutes: number;
}

export interface DayScheduleEntry {
  earnMinutes: number;
  playMinutes: number;
  screenTimeMinutes?: number; // per-day screen time limit override
  goldPerMinuteTrade?: number; // coin-to-minutes rate override for this day
}

export interface DaySchedule {
  [day: string]: DayScheduleEntry; // "monday", "tuesday", etc.
}

export const MAX_GOLD_PER_TASK = 20;
export const MAX_DAILY_TASK_GOLD = 80;
export const GOLD_REAL_VALUE_PENCE = 10; // 1 coin = 10p

export interface OffPhoneEarner {
  id: string;
  name: string;
  icon: string;
  activityMinutes: number; // how many minutes of this activity
  minutesPerUnit: number; // how many gold coins earned per completion
  taskDescription?: string; // what the child needs to do to earn the bonus
  logoId?: string; // id from TASK_LOGOS catalog (Adventures-style logo)
}

export interface MarketplaceConfig {
  enabledCategories: string[]; // category IDs from VAULT_CATEGORIES
  disabledRewardIds: string[]; // specific reward IDs hidden within enabled categories
}

export interface BedtimeConfig {
  enabled: boolean;
  startTime: string; // "HH:MM" format e.g. "08:00"
  endTime: string;   // "HH:MM" format e.g. "20:00"
}

export interface ChildConfig {
  childId: string;
  apps: AppItem[];
  ratio: RatioConfig;
  daySchedules: DaySchedule;
  offPhoneEarners: OffPhoneEarner[];
  screenTimeLimitMinutes: number;
  rewardItems: RewardItem[];
  goldPerMinuteTrade: number; // minutes per exchange (default 2)
  goldPerExchange: number; // gold coins per exchange (default 1)
  marketplace?: MarketplaceConfig;
  bedtime?: BedtimeConfig;
  requireCrewApproval?: boolean; // if true, parent must approve before a mining crew challenge starts
  requireFriendApproval?: boolean; // if true, parent must approve new friend requests; otherwise auto-approve
}

export interface EarnedSession {
  childId: string;
  source: string; // app name or off-phone earner name
  earnedMinutes: number;
  playMinutesGranted: number;
  timestamp: string;
}

export interface RewardItem {
  id: string;
  name: string;
  icon: string;
  goldCost: number;
  description?: string;
}

export interface StreakData {
  daily: { lastDate: string; count: number };
  perActivity: Record<string, { lastDate: string; count: number }>; // keyed by earner name
}

export interface GoldTransaction {
  type: "daily" | "trade" | "bonus_activity" | "milestone" | "purchase";
  amount: number; // positive = earned, negative = spent
  description: string;
  timestamp: string;
}

export interface PendingApproval {
  id: string;
  earnerName: string;
  earnerIcon: string;
  goldAmount: number;
  timestamp: string;
  status: "pending" | "approved" | "rejected";
  childPopupSeenAt?: string;
}

export interface LinkPreview {
  title?: string;
  domain?: string;
  image?: string;
  logo?: string;
  icon?: string;
  favicon?: string;
  price?: string;
  url: string;
}

export interface RewardPreview {
  title?: string;
  domain?: string;
  url: string;
  image?: string;
  logo?: string;
  icon?: string;
  favicon?: string;
}

export interface ProductMetadata {
  productTitle?: string;
  productUrl?: string;
  productDomain?: string;
  productImage?: string;
  productPriceValue?: number;   // numeric price e.g. 12.99
  productCurrency?: string;     // e.g. "GBP", "USD"
  suggestedCoinValue?: number;  // round(priceValue * 10) for GBP
  priceDetectedAt?: string;     // ISO timestamp
}

export interface RewardRequest {
  id: string;
  childId: string;
  name: string;
  description?: string;
  icon: string;
  itemLink?: string;
  linkPreview?: LinkPreview;
  rewardPreview?: RewardPreview;
  productMeta?: ProductMetadata;
  status: "pending" | "approved" | "denied" | "ready_to_claim" | "claimed";
  goldCost?: number; // assigned by parent on approval
  contributedGold?: number; // gold contributed toward this goal (0 until child contributes)
  claimedAt?: string; // timestamp when parent confirmed delivery
  timestamp: string;
  isCrewRequest?: boolean;
  crewMemberIds?: string[]; // child profile IDs (includes requester)
  crewLocked?: boolean; // true once parent approves
  // Shared reward request fields
  sharedStatus?: "waiting" | "active" | "solo_converted"; // waiting = friend hasn't responded yet
  invitedFriendId?: string;
  invitedFriendName?: string;
  invitedFriendAvatar?: string;
  inviteTimestamp?: string; // when the invite was sent (for 48h auto-convert)
  friendDeclined?: boolean; // true if friend explicitly declined
}

export interface ChildState {
  childId: string;
  earnedPlayMinutes: number;
  usedPlayMinutes: number;
  totalScreenTimeToday: number;
  sessions: EarnedSession[];
  gold: number;
  goldTransactions: GoldTransaction[];
  streaks: StreakData;
  purchasedRewards: string[]; // reward item IDs
  redeemedRewards?: string[]; // descriptors from purchasedRewards that the parent has marked as redeemed
  pendingApprovals: PendingApproval[];
  rewardRequests?: RewardRequest[];
  parentNotifications?: ParentNotification[];
  lastInboxSeenAt?: string;
}

export interface ParentNotification {
  id: string;
  type: "reward_unlocked" | "crew_update" | "crew_created" | "crew_member_joined" | "crew_member_left" | "parent_goal_added" | "reward_approved" | "reward_denied" | "crew_approved" | "crew_denied";
  childName: string;
  rewardName: string;
  goldSpent: number;
  timestamp: string;
  seen?: boolean;
  // Crew-specific fields
  crewId?: string;
  crewName?: string;
  crewGoalName?: string;
  crewGoalIcon?: string;
  crewGoalRequired?: number;
  crewTotalGold?: number;
  crewMemberCount?: number;
  crewMembers?: { name: string; avatar: string; contributed: number }[];
  crewApprovalStatus?: "pending" | "approved" | "denied";
  // Member join/leave fields
  joinedMemberName?: string;
  joinedMemberAvatar?: string;
  leftMemberName?: string;
}

// ── Sample Data ──

export const SAMPLE_APPS: AppItem[] = [
  { id: "1", name: "Khan Academy Kids", icon: "📚", category: "earn" },
  { id: "2", name: "Duolingo", icon: "🦉", category: "earn" },
  { id: "3", name: "Scratch Jr", icon: "🐱", category: "earn" },
  { id: "4", name: "PBS Kids", icon: "🎓", category: "earn" },
  { id: "5", name: "Drawing Desk", icon: "🎨", category: "earn" },
  { id: "6", name: "YouTube Kids", icon: "📺", category: "play" },
  { id: "7", name: "Roblox", icon: "🎮", category: "play" },
  { id: "8", name: "Minecraft", icon: "⛏️", category: "play" },
  { id: "9", name: "Netflix", icon: "🍿", category: "play" },
  { id: "10", name: "TikTok", icon: "🎵", category: "play" },
];

export const DEFAULT_OFF_PHONE_EARNERS: OffPhoneEarner[] = [
  { id: "o1", name: "Reading", icon: "📖", activityMinutes: 1, minutesPerUnit: 10 },
  { id: "o2", name: "Chores", icon: "🧹", activityMinutes: 1, minutesPerUnit: 10 },
  { id: "o3", name: "Exercise", icon: "🏃", activityMinutes: 1, minutesPerUnit: 10 },
  { id: "o4", name: "Music", icon: "🎵", activityMinutes: 1, minutesPerUnit: 10 },
];

export const DAYS_OF_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
export const DAY_LABELS: Record<string, string> = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed",
  thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun",
};
